import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useT } from "@/i18n/LanguageContext";

export const HOLD_RESPONDER_ROLES = ["shipping_order", "back_office", "super_admin"];
export const STORE_ACK_ROLES = ["packing_orders", "inventory_packing", "back_office", "super_admin"];
// Who physically holds the box: CHINA → Packing Orders; CANADA / USA → Inventory Packing (receives in Canada).
export const stationForWarehouse = (wh) => (String(wh || "china").toLowerCase() === "china" ? "packing_orders" : "inventory_packing");
export const STATION_LABEL = { packing_orders: "Packing Orders (China)", inventory_packing: "Inventory Packing (Canada)" };
export const canAckStore = (user, wh) =>
  user?.role === "super_admin" || user?.role === "back_office" || user?.role === stationForWarehouse(wh);

export const canDecideReorder = (user, factory) =>
  user?.role === "super_admin" || (user?.role === (factory || "factory_1") && (user?.sub_role || "administrator") === "administrator");

const ACTION_META = {
  accept: { icon: "✓", label: "Accepted", cls: "bg-emerald-600 hover:bg-emerald-700", confirm: "Confirm Accepted",
            hint: "The packing station will be told to continue packing this order." },
  send_back: { icon: "↩", label: "Send Back", cls: "bg-red-600 hover:bg-red-700", confirm: "Confirm Send Back",
               hint: "The order moves to Production · QUALITY CONTROL as PO-XXXX-D1. The factory receives the issue note and photos; the packing station is told to send the item back." },
  reorder: { icon: "🔁", label: "Re-order", cls: "bg-slate-900 hover:bg-slate-800", confirm: "Request Re-order",
             hint: "A new order PO-XXXX-R goes to Production and is billed again. Wrong colour → the set is stored in the CHINA warehouse under the same catalog #; any other reason → the set goes to SPARE PARTS. The packing station is told where to put it. Needs the Factory One administrator's approval." },
};

// "✓ Accepted" / "↩ Send Back" / "🔁 Re-order" + note dialog. Used on the
// Dashboard alert row and on the order page while an order is ON HOLD.
export function HoldRespondButtons({ orderId, poNumber, size = "sm", onDone, reorderStatus, reorderNote, reorderBy }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [action, setAction] = useState(null);
  const [note, setNote] = useState("");
  const [color, setColor] = useState("");
  const [reason, setReason] = useState("");
  const [reasonText, setReasonText] = useState("");
  const { data: colors = [] } = useQuery({
    queryKey: ["inventory-colors"],
    queryFn: async () => (await api.get("/inventory/colors")).data?.colors || [],
    enabled: action === "reorder",
    staleTime: 60 * 60 * 1000,
  });
  const { data: reasons = [] } = useQuery({
    queryKey: ["reorder-reasons"],
    queryFn: async () => (await api.get("/reorder-reasons")).data?.reasons || [],
    enabled: action === "reorder",
    staleTime: 60 * 60 * 1000,
  });
  const isWrongColor = reason === "wrong_color";
  const reorderReady = !!reason && (!isWrongColor || !!color) && (reason !== "other" || !!reasonText.trim());
  const respond = useMutation({
    mutationFn: () => api.post(`/orders/${orderId}/hold-respond`, {
      action, note: note.trim() || null,
      received_color: action === "reorder" && isWrongColor ? color : null,
      reorder_reason: action === "reorder" ? reason : null,
      reorder_reason_text: action === "reorder" && reason === "other" ? reasonText.trim() : null,
    }),
    onSuccess: (r) => {
      qc.invalidateQueries();
      const res = r?.data?.resolution;
      toast.success(res === "accepted" ? t("Accepted — packing station told to continue")
        : res === "reorder_pending" ? t("Re-order requested — waiting for Factory One administrator approval")
        : `${t("Sent back to Factory — Quality Control")} · ${r?.data?.po_number || ""}`);
      setAction(null); setNote(""); setColor(""); setReason(""); setReasonText("");
      onDone?.(r?.data);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  if (reorderStatus === "pending") {
    return (
      <span className="inline-flex items-center rounded-md border border-slate-300 bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700" data-testid={`hold-reorder-pending-${orderId}`}>
        🔁 {t("Re-order requested")}{reorderBy ? ` · ${reorderBy}` : ""} — {t("waiting for Factory One administrator")}
      </span>
    );
  }
  const meta = action ? ACTION_META[action] : null;
  return (
    <>
      {reorderStatus === "rejected" && (
        <span className="inline-flex items-center rounded-md border border-red-300 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-800" data-testid={`hold-reorder-rejected-${orderId}`} title={reorderNote || ""}>
          ✕ {t("Re-order rejected")}{reorderBy ? ` · ${reorderBy}` : ""}{reorderNote ? `: "${reorderNote}"` : ""}
        </span>
      )}
      {Object.entries(ACTION_META).map(([key, m]) => (
        <Button key={key} size={size} data-testid={`hold-${key === "send_back" ? "sendback" : key}-${orderId}`} onClick={() => setAction(key)}
                className={`${m.cls} text-white`}>
          {m.icon} {t(m.label)}
        </Button>
      ))}
      <Dialog open={!!action} onOpenChange={(v) => { if (!v) setAction(null); }}>
        <DialogContent className="max-w-md" data-testid="hold-respond-dialog">
          <DialogHeader>
            <DialogTitle>{meta ? `${meta.icon} ${t(meta.label)}` : ""} · {poNumber}</DialogTitle>
          </DialogHeader>
          {meta && (
            <div className="space-y-3">
              <p className="text-xs text-slate-600">{t(meta.hint)}</p>
              {action === "reorder" && (
                <div>
                  <Label className="text-xs">{t("Why is it re-ordered?")} *</Label>
                  <select
                    data-testid="hold-reorder-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="mt-1 w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
                  >
                    <option value="">{t("Select reason…")}</option>
                    {reasons.map((r) => <option key={r.code} value={r.code}>{t(r.label)}{r.code === "other" ? "…" : ""}</option>)}
                  </select>
                  {reason === "other" && (
                    <input
                      data-testid="hold-reorder-reason-text"
                      value={reasonText}
                      onChange={(e) => setReasonText(e.target.value)}
                      placeholder={t("Type the reason")}
                      className="mt-2 w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
                    />
                  )}
                  {reason && (
                    <p className="mt-1.5 text-[11px] font-semibold" data-testid="hold-reorder-store-hint">
                      {isWrongColor
                        ? <span className="text-teal-800">📦 {t("The set is stored in the CHINA warehouse under the same Catalog #.")}</span>
                        : <span className="text-amber-800">🧩 {t("The set goes to SPARE PARTS — not back into sellable stock.")}</span>}
                    </p>
                  )}
                </div>
              )}
              {action === "reorder" && isWrongColor && (
                <div>
                  <Label className="text-xs">{t("Colour actually received")} *</Label>
                  <select
                    data-testid="hold-reorder-color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="mt-1 w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
                  >
                    <option value="">{t("Select colour…")}</option>
                    {colors.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              )}
              <div>
                <Label className="text-xs">{t("Note (optional)")}</Label>
                <Textarea data-testid="hold-respond-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("Shown to the packing station and the factory")} />
              </div>
              <Button data-testid="hold-respond-submit" disabled={respond.isPending || (action === "reorder" && !reorderReady)} onClick={() => respond.mutate()}
                      className={`w-full text-white ${meta.cls}`}>
                {meta.icon} {t(meta.confirm)}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// Factory One administrator (or Super Admin): approve / reject a re-order.
export function ReorderDecideButtons({ orderId, poNumber, size = "sm", onDone }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [decision, setDecision] = useState(null);
  const [note, setNote] = useState("");
  const decide = useMutation({
    mutationFn: () => api.post(`/orders/${orderId}/reorder-${decision}`, { note: note.trim() || null }),
    onSuccess: (r) => {
      qc.invalidateQueries();
      toast.success(decision === "approve" ? `${t("Re-order approved")} → ${r?.data?.new_po || ""}` : t("Re-order rejected — hold stays open"));
      setDecision(null); setNote("");
      onDone?.(r?.data);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <>
      <Button size={size} data-testid={`reorder-approve-${orderId}`} onClick={() => setDecision("approve")} className="bg-emerald-600 hover:bg-emerald-700 text-white">
        ✓ {t("Approve re-order")}
      </Button>
      <Button size={size} data-testid={`reorder-reject-${orderId}`} onClick={() => setDecision("reject")} className="bg-red-600 hover:bg-red-700 text-white">
        ✕ {t("Reject")}
      </Button>
      <Dialog open={!!decision} onOpenChange={(v) => { if (!v) setDecision(null); }}>
        <DialogContent className="max-w-md" data-testid="reorder-decide-dialog">
          <DialogHeader>
            <DialogTitle>{decision === "approve" ? `✓ ${t("Approve re-order")}` : `✕ ${t("Reject re-order")}`} · {poNumber}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-600">
              {decision === "approve"
                ? t("A new order PO-XXXX-R is created in Production and billed again. Wrong colour → the set goes to Inventory (CHINA warehouse); any other reason → SPARE PARTS. The packing station is told where to put it.")
                : t("The hold stays open — Shipping Desk / Back Office can still Accept or Send Back.")}
            </p>
            <div>
              <Label className="text-xs">{t("Note (optional)")}</Label>
              <Textarea data-testid="reorder-decide-note" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <Button data-testid="reorder-decide-submit" disabled={decide.isPending} onClick={() => decide.mutate()}
                    className={`w-full text-white ${decision === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`}>
              {decision === "approve" ? `✓ ${t("Confirm approval")}` : `✕ ${t("Confirm rejection")}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Packing station: confirm the received set is stored (warehouse) / put in spare parts.
export function StoreAckButton({ orderId, size = "sm", onDone, spare = false }) {
  const { t } = useT();
  const qc = useQueryClient();
  const ack = useMutation({
    mutationFn: () => api.post(`/orders/${orderId}/store-ack`),
    onSuccess: () => { qc.invalidateQueries(); toast.success(spare ? t("In spare parts — thank you") : t("Stored — thank you")); onDone?.(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <Button size={size} data-testid={`store-ack-${orderId}`} disabled={ack.isPending} onClick={() => ack.mutate()}
            className={spare ? "bg-amber-700 hover:bg-amber-800 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}>
      {spare ? <>🧩 ✓ {t("In spare parts")}</> : <>📦 ✓ {t("Stored")}</>}
    </Button>
  );
}

export function QcResolveButton({ orderId, size = "sm", onDone }) {
  const { t } = useT();
  const qc = useQueryClient();
  const resolve = useMutation({
    mutationFn: () => api.post(`/orders/${orderId}/qc-resolve`),
    onSuccess: () => { qc.invalidateQueries(); toast.success(t("Quality Control resolved")); onDone?.(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <Button size={size} data-testid={`qc-resolve-${orderId}`} disabled={resolve.isPending} onClick={() => resolve.mutate()}
            className="bg-emerald-600 hover:bg-emerald-700 text-white">
      ✓ {t("QC Resolved")}
    </Button>
  );
}
